import React from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * Compact page selector with Prev / windowed pages / Next.
 * Renders nothing when totalPages <= 1.
 *
 * Props:
 *   page          current 1-indexed page
 *   pageSize      rows per page
 *   total         total rows in the filtered set
 *   totalPages    number of pages
 *   onPageChange  (nextPage:number) => void
 *   testIdPrefix  test-id namespace, e.g. "pos"
 */
export default function Pagination({
  page = 1,
  pageSize = 25,
  total = 0,
  totalPages = 1,
  onPageChange,
  testIdPrefix = "pg",
}) {
  if (!totalPages || totalPages <= 1) {
    // Still render a lightweight footer so users see the total
    if (total > 0) {
      return (
        <div
          className="flex items-center justify-between px-4 py-2 text-xs text-neutral-500 border-t border-neutral-100"
          data-testid={`${testIdPrefix}-pagination-footer`}
        >
          <span>Showing {total} of {total}</span>
        </div>
      );
    }
    return null;
  }

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // Windowed page numbers: 1 … page-1 page page+1 … totalPages
  const nums = new Set([1, totalPages, page - 1, page, page + 1]);
  const pages = [...nums].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);

  return (
    <div
      className="flex items-center justify-between px-4 py-2 border-t border-neutral-100 bg-white"
      data-testid={`${testIdPrefix}-pagination`}
    >
      <span className="text-xs text-neutral-500 tabular-nums">
        Showing {start}–{end} of {total}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex items-center h-8 w-8 justify-center border border-neutral-200 rounded-sm text-neutral-600 disabled:opacity-40 hover:bg-neutral-50"
          data-testid={`${testIdPrefix}-pg-prev`}
        >
          <CaretLeft size={14} />
        </button>
        {pages.map((n, i) => {
          const showEllipsis = i > 0 && n - pages[i - 1] > 1;
          return (
            <React.Fragment key={n}>
              {showEllipsis && <span className="px-1 text-neutral-400">…</span>}
              <button
                onClick={() => onPageChange(n)}
                className={`inline-flex items-center h-8 min-w-[32px] px-2 justify-center border rounded-sm tabular-nums text-xs ${
                  n === page
                    ? "border-primary bg-primary text-white font-medium"
                    : "border-neutral-200 text-neutral-700 hover:bg-neutral-50"
                }`}
                data-testid={`${testIdPrefix}-pg-${n}`}
              >
                {n}
              </button>
            </React.Fragment>
          );
        })}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex items-center h-8 w-8 justify-center border border-neutral-200 rounded-sm text-neutral-600 disabled:opacity-40 hover:bg-neutral-50"
          data-testid={`${testIdPrefix}-pg-next`}
        >
          <CaretRight size={14} />
        </button>
      </div>
    </div>
  );
}
